import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useCategory, useCreateCategory, useUpdateCategory } from '../api/admin-api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { I18nFieldsSection } from '../components/I18nFieldsSection'
import { toast } from 'sonner'

type CategoryFormData = {
  id: string
  name: string
  description: string
  nameI18n: Record<string, string>
  descriptionI18n: Record<string, string>
  listOrder: number
  enabled: boolean
}

export function CategoryForm() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: category } = useCategory(id!)
  const createMutation = useCreateCategory()
  const updateMutation = useUpdateCategory(id!)

  const baseFormData = useMemo<CategoryFormData>(
    () =>
      category
        ? {
            id: category.id,
            name: category.name,
            description: category.description || '',
            nameI18n: category.nameI18n ?? {},
            descriptionI18n: category.descriptionI18n ?? {},
            listOrder: category.listOrder,
            enabled: category.enabled,
          }
        : {
            id: '',
            name: '',
            description: '',
            nameI18n: {},
            descriptionI18n: {},
            listOrder: 0,
            enabled: true,
          },
    [category],
  )
  const [draftFormData, setDraftFormData] = useState<CategoryFormData | null>(null)
  const formData = draftFormData ?? baseFormData

  const updateField = <K extends keyof CategoryFormData>(key: K, value: CategoryFormData[K]) => {
    setDraftFormData((prev) => ({ ...(prev ?? baseFormData), [key]: value }))
  }

  const updateI18nField = (field: 'nameI18n' | 'descriptionI18n', lang: string, text: string) => {
    setDraftFormData((prev) => {
      const base = prev ?? baseFormData
      return { ...base, [field]: { ...base[field], [lang]: text } }
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    try {
      if (isEdit) {
        await updateMutation.mutateAsync({
          name: formData.name,
          description: formData.description,
          nameI18n: formData.nameI18n,
          descriptionI18n: formData.descriptionI18n,
          listOrder: formData.listOrder,
          enabled: formData.enabled,
        })
        toast.success('템플릿 카테고리가 수정되었습니다')
      } else {
        await createMutation.mutateAsync(formData)
        toast.success('템플릿 카테고리가 생성되었습니다')
      }
      // 수정 시 상세 페이지로, 생성 시 목록으로 이동
      if (isEdit) {
        navigate(`/admin/categories/${id}/view`)
      } else {
        navigate('/admin/categories')
      }
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : '저장 중 오류가 발생했습니다')
    }
  }

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-heading tracking-tight">
          {isEdit ? '템플릿 카테고리 수정' : '새 템플릿 카테고리'}
        </h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6 bg-white p-6 rounded-lg border">
        {!isEdit && (
          <div className="space-y-2">
            <Label htmlFor="id">ID * (영문 소문자, 언더스코어)</Label>
              <Input
                id="id"
                value={formData.id}
                onChange={(e) => updateField('id', e.target.value)}
                required
                pattern="[a-z_]+"
                placeholder="featured, christmas 등"
              />
          </div>
        )}

        <div className="space-y-2">
          <Label>이름 *</Label>
          <Input
            value={formData.name}
            onChange={(e) => updateField('name', e.target.value)}
            required
            placeholder="추천, 크리스마스 등"
          />
        </div>

        <div className="space-y-2">
          <Label>설명</Label>
          <Textarea
            value={formData.description}
            onChange={(e) => updateField('description', e.target.value)}
            rows={2}
          />
        </div>

        <I18nFieldsSection
          value={{ nameI18n: formData.nameI18n, descriptionI18n: formData.descriptionI18n }}
          onChange={updateI18nField}
          withDescription
        />

        <div className="space-y-2">
          <Label>정렬 순서</Label>
          <Input
            type="number"
            value={formData.listOrder}
            onChange={(e) => updateField('listOrder', Number.parseInt(e.target.value, 10) || 0)}
          />
        </div>

        <div className="flex items-center space-x-2">
          <input
            type="checkbox"
            checked={formData.enabled}
            onChange={(e) => updateField('enabled', e.target.checked)}
            className="w-4 h-4"
          />
          <Label>활성화</Label>
        </div>

        <div className="flex space-x-4">
          <Button type="submit" disabled={createMutation.isPending || updateMutation.isPending}>
            {isEdit ? '수정' : '생성'}
          </Button>
          <Button type="button" variant="outline" onClick={() => navigate(-1)}>
            취소
          </Button>
        </div>
      </form>
    </div>
  )
}
